import React, { useState } from 'react';
import { Trophy, Zap, AlertTriangle, Eye, ArrowRight, Loader2, Clock, CheckCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

/**
 * BattleCard v3.3 (Atomic Truth UI - Iron Truth Protocol + Dominate Logic)
 * * Changelog v3.3:
 * - Full Iron Truth: Status derived ONLY from 'audit' phase.
 * - Dominate Logic: Ranks 4-10 in ANY engine trigger 'Visible' (Orange) status.
 * - Attack results are strictly visual (do not affect card color).
 */
const BattleCard = ({ battle, onView, onResolve, isFixing: propsIsFixing, uiStatus, computedRank, defaultLocation }) => {
    // --- 1. STATE & DATA PREP ---
    const [activeTab, setActiveTab] = useState('all');
    const [localFixing, setLocalFixing] = useState(false);
    const isFixing = propsIsFixing || localFixing;

    // Safety Cleanups
    const rawTitle = battle.keyword || battle.query || battle.title || "Unknown Topic";
    let displayTitle = rawTitle
        .replace(/\[.*?\]/g, '')
        .replace(/^[\p{Emoji}\s]+/gu, '')
        .replace(/^Auto:\s*/i, '')
        .trim() || "Unknown Keyword";

    // Location Fallback
    let displayLocation = battle.location;
    if (!displayLocation || displayLocation === 'Local Market' || displayLocation.includes('Unknown')) {
        if (battle.profile?.company_name?.includes('(')) {
            const match = battle.profile.company_name.match(/\((.*?)\)/);
            if (match) displayLocation = match[1];
        }
        if (!displayLocation) displayLocation = defaultLocation || "Local Market";
    }

    // --- 2. DATA PROCESSING (Map & Unique Tabs) ---
    const atomicResults = Array.isArray(battle.results) ? battle.results : [];

    // Create Lookup Map for O(1) Access and Rank Extraction
    const resultsMap = {};
    atomicResults.forEach(r => {
        if (r.engine) resultsMap[r.engine.toLowerCase()] = r;
    });

    // Unique Tabs Logic: 'all' + valid engines
    const uniqueTabs = ['all', ...new Set(Object.keys(resultsMap).filter(k => k !== 'all'))];

    // Helper: Get Label with Rank
    const getTabLabel = (key) => {
        if (key === 'all') return '⚡ All';

        let label = key;
        let icon = "⚡";

        const lowerKey = key.toLowerCase();
        if (lowerKey === 'google_organic') { label = "Google"; icon = "🇬"; }
        else if (lowerKey === 'gpt4' || lowerKey === 'gpt-4') { label = "GPT-4"; icon = "🤖"; }
        else if (lowerKey === 'gemini') { label = "Gemini"; icon = "💎"; }
        else if (lowerKey === 'deepseek' || lowerKey.includes('deepseek')) { label = "DeepSeek"; icon = "🧠"; }
        else if (lowerKey === 'grok') { label = "Grok"; icon = "🚀"; }
        else if (lowerKey === 'claude') { label = "Claude"; icon = "🧠"; }

        // Add Rank if available (Prefer Audit rank if available, else generic)
        const res = resultsMap[lowerKey];
        const rankDisplay = res && res.rank_position ? ` #${res.rank_position}` : '';

        return `${icon} ${label}${rankDisplay}`;
    };

    // --- 3. STATUS LOGIC (IRON TRUTH PROTOCOL) ---
    // СТРОГОЕ ПРАВИЛО: Статус карточки (Win/Loss) зависит ТОЛЬКО от фазы 'audit'.
    // Любые успехи, достигнутые в фазе 'attack' (уговоры AI), игнорируются для статистики.

    // 1. Ищем результаты честного аудита (игнорируем attack/persuasion)
    const googleAudit = atomicResults.find(r =>
        r.engine === 'google_organic' && r.round_phase === 'audit'
    );
    // Дополнительно: проверяем изначальное мнение GPT (до нашего влияния)
    const gptAudit = atomicResults.find(r =>
        (r.engine === 'gpt4' || r.engine === 'gpt-4') && r.round_phase === 'audit'
    );

    // 2. Определяем "Железный Ранг" (Iron Rank)
    // Если в аудите нас не нашли — rank null (это автоматически LOSS)
    const googleRank = googleAudit ? googleAudit.rank_position : null;
    const gptRank = gptAudit ? gptAudit.rank_position : null;

    // 3. Вычисляем честный статус
    let cardStatus = 'loss'; // По умолчанию - проигрыш (пессимистичный сценарий)
    let displayRank = null;

    // --- TIER 1: ABSOLUTE WIN (1-3) ---
    // Зеленая карточка: Мы в Топ-3 в Google или GPT
    if (googleRank && googleRank <= 3) {
        cardStatus = 'win';
        displayRank = googleRank;
    }
    else if (!googleRank && gptRank && gptRank <= 3) {
        cardStatus = 'win'; // GPT Top-3 тоже считается победой (AI Dominance)
        displayRank = gptRank;
    }

    // --- TIER 2: VISIBLE / DOMINATE (4-10) ---
    // Оранжевая карточка: Мы на первой странице (Топ-10) в любой из систем
    else if ((googleRank && googleRank <= 10) || (gptRank && gptRank <= 10)) {
        cardStatus = 'visible';
        displayRank = googleRank || gptRank;
    }

    // --- TIER 3: LOSS (>10 or Null) ---
    // Красная карточка: Остается 'loss'

    // 4. Настройка цветов (Зависит ТОЛЬКО от аудита)
    const isLoss = cardStatus === 'loss';
    const isVisible = cardStatus === 'visible';
    const isWin = cardStatus === 'win';

    // Цвета:
    // WIN (1-3) = Зеленый (#10b981)
    // VISIBLE (4-10) = Оранжевый/Желтый (#f59e0b)
    // LOSS (>10) = Красный (#ef4444)

    const borderColor = isLoss ? '#fecaca' : (isVisible ? '#fcd34d' : '#bef264');
    const sideColor = isLoss ? '#ef4444' : (isWin ? '#10b981' : '#f59e0b');

    // Цвет бейджа статуса
    const badgeBg = isLoss ? '#fef2f2' : (isWin ? '#f0fdf4' : '#fffbeb');
    const badgeText = isLoss ? '#b91c1c' : (isWin ? '#15803d' : '#b45309');
    const badgeBorder = isLoss ? '#fecaca' : (isWin ? '#bbf7d0' : '#fde68a');
    const statusIcon = isLoss ? '⚠️ LOSS' : (isWin ? '🏆 WIN' : '⚡ VISIBLE');

    // 5. Итоговый ранг для бейджа
    const finalRankBadge = displayRank ? `#${displayRank}` : '';

    // --- 4. RENDER HELPERS ---

    // Render a Single Result Block
    const renderResultBlock = (res, idx) => {
        const isAudit = res.round_phase === 'audit';
        const isWin = res.status === 'WIN';

        const bg = isAudit ? '#f8fafc' : (isWin ? '#f0fdf4' : '#fff');
        const border = isAudit ? '#e2e8f0' : (isWin ? '#bef264' : '#f1f5f9');
        const icon = isAudit ? '🔒' : (isWin ? '🏆' : '⚠️');

        // Label Logic
        let statusLabel = '';
        if (isAudit) statusLabel = 'Honest Audit (Truth)';
        else statusLabel = `Attack Result: ${res.status}`;

        let content = "No content available";
        if (res.raw_response) {
            let parsed = res.raw_response;
            // Eager parse if it is a string that looks like JSON
            if (typeof parsed === 'string' && (parsed.startsWith('{') || parsed.startsWith('['))) {
                try { parsed = JSON.parse(parsed); } catch (e) { }
            }

            if (parsed && typeof parsed === 'object') {
                if (parsed.status === 'error' || parsed.error) {
                    const errMsg = parsed.error || parsed.message || JSON.stringify(parsed);
                    if (String(errMsg).includes('429') || String(errMsg).includes('exhausted')) {
                        content = "⚠️ AI Provider Busy (Rate Limit Exceeded). System will retry automatically.";
                    } else {
                        content = `❌ Analysis Error: ${errMsg}`;
                    }
                } else if (parsed.ai_response) {
                    content = parsed.ai_response;
                } else if (parsed.text) {
                    content = parsed.text;
                } else {
                    content = JSON.stringify(parsed);
                }
            } else {
                content = String(parsed);
            }
        }

        // Remove raw JSON characters if they leaked
        if (content.startsWith('"') && content.endsWith('"')) {
            content = content.slice(1, -1);
        }

        const preview = content.length > 200 ? content.substring(0, 200) + '...' : content;

        return (
            <div key={idx} style={{
                background: bg, border: `1px solid ${border}`, borderRadius: '8px',
                padding: '12px', marginBottom: '8px', fontSize: '0.85rem', color: '#334155'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <div style={{ fontWeight: '700', fontSize: '0.75rem', textTransform: 'uppercase', color: isAudit ? '#64748b' : '#15803d', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>{icon}</span>
                        {statusLabel}
                    </div>
                    {res.rank_position && (
                        <div style={{ fontSize: '0.75rem', fontWeight: '700', color: isAudit ? '#64748b' : '#15803d' }}>
                            Rank #{res.rank_position}
                        </div>
                    )}
                </div>
                <div style={{ lineHeight: '1.4', fontFamily: 'monospace', fontSize: '0.8rem', background: 'rgba(255,255,255,0.5)', padding: '8px', borderRadius: '4px' }}>
                    {preview}
                </div>
            </div>
        );
    };

    // Render Content Area based on Selection
    const renderContent = () => {
        // Mode 1: All
        if (activeTab === 'all') {
            if (atomicResults.length === 0) return renderLegacyFallback();
            return (
                <div>
                    {/* Sort: Audit First */}
                    {[...atomicResults]
                        .sort((a, b) => (a.round_phase === 'audit' ? -1 : 1))
                        .map((res, i) => renderResultBlock(res, i))
                    }
                </div>
            );
        }

        // Mode 2: Specific Tab
        const targetRes = resultsMap[activeTab];

        // Handle case sensitivity fallback (e.g. gpt4 vs gpt-4)
        if (!targetRes && (activeTab === 'gpt4' || activeTab === 'gpt-4')) {
            const alt = resultsMap['gpt4'] || resultsMap['gpt-4'];
            if (alt) return renderResultBlock(alt, activeTab);
        }

        if (!targetRes) {
            return (
                <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem', background: '#f8fafc', borderRadius: '8px' }}>
                    ⏳ Data for <b>{activeTab}</b> is pending or in legacy format.<br />
                    Please check the "All" tab.
                </div>
            );
        }

        return renderResultBlock(targetRes, activeTab);
    };

    const renderLegacyFallback = () => {
        const competitors = battle.competitors || [];
        const text = battle.message_content || battle.initial_message || "No data.";
        const isClean = text.startsWith('{') ? "Structured Data" : text.substring(0, 100) + "...";
        return (
            <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', color: '#64748b', fontSize: '0.85rem' }}>
                <div style={{ marginBottom: '8px', fontWeight: '600' }}>Legacy View</div>
                {competitors.length > 0 && (
                    <div style={{ marginBottom: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {competitors.slice(0, 3).map((c, i) => (
                            <span key={i} style={{ fontSize: '0.75rem', background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', color: '#475569' }}>
                                {typeof c === 'string' ? c : c.name}
                            </span>
                        ))}
                    </div>
                )}
                <div>{isClean}</div>
            </div>
        );
    };

    // --- 5. MAIN RENDER ---
    return (
        <div style={{
            display: 'flex', flexDirection: 'row', alignItems: 'stretch',
            backgroundColor: isLoss ? '#fff1f2' : 'white',
            border: `1px solid ${borderColor}`,
            borderLeft: `4px solid ${sideColor}`,
            borderRadius: '8px', marginBottom: '12px',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', overflow: 'hidden'
        }}>
            {/* LEFT CONTENT */}
            <div style={{ flex: 1, padding: '20px', minWidth: 0 }}>
                {/* Header Row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <div style={{
                        background: badgeBg,
                        color: badgeText,
                        border: `1px solid ${badgeBorder}`,
                        padding: '4px 10px', borderRadius: '999px',
                        fontSize: '11px', fontWeight: '700', textTransform: 'uppercase',
                        display: 'flex', alignItems: 'center', gap: '4px'
                    }}>
                        {statusIcon}
                        {finalRankBadge}
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Clock size={12} />
                        {battle.created_at ? formatDistanceToNow(new Date(battle.created_at), { addSuffix: true }) : 'Just now'}
                    </div>
                </div>

                {/* Title */}
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {battle.title && battle.title.toLowerCase().includes('auto') && <Zap size={16} fill="#3b82f6" color="#3b82f6" />}
                    {displayTitle}
                </h3>
                <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>📍 {displayLocation}</div>

                {/* TABS (Scrollable) */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', overflowX: 'auto', paddingBottom: '4px' }}>
                    {uniqueTabs.map(tabKey => {
                        const isActive = activeTab === tabKey;
                        return (
                            <button
                                key={tabKey}
                                onClick={() => setActiveTab(tabKey)}
                                style={{
                                    padding: '4px 10px',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    fontWeight: isActive ? '700' : '500',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    transition: 'all 0.2s',
                                    backgroundColor: isActive ? '#e0f2fe' : '#f8fafc',
                                    color: isActive ? '#0369a1' : '#64748b',
                                    border: isActive ? '1px solid #bae6fd' : '1px solid transparent'
                                }}
                            >
                                {getTabLabel(tabKey)}
                            </button>
                        );
                    })}
                </div>

                {/* CONTENT AREA */}
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {renderContent()}
                </div>
            </div>

            {/* RIGHT ACTIONS */}
            <div style={{ width: '200px', flex: '0 0 200px', borderLeft: '1px solid #f1f5f9', background: '#f8fafc', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', justifyContent: 'center' }}>
                <button
                    onClick={() => onView(battle)}
                    style={{ width: '100%', padding: '10px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', color: '#475569', fontWeight: '600', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }}
                >
                    <Eye size={15} /> View Report
                </button>

                {isLoss && (
                    <button
                        onClick={() => {
                            if (!isFixing) {
                                setLocalFixing(true);
                                if (onResolve) onResolve(battle);
                            }
                        }}
                        disabled={isFixing}
                        style={{
                            width: '100%', padding: '10px',
                            background: isFixing ? '#e2e8f0' : '#0f172a',
                            border: 'none', borderRadius: '6px',
                            color: isFixing ? '#94a3b8' : 'white',
                            fontWeight: '600', fontSize: '13px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                            cursor: isFixing ? 'default' : 'pointer'
                        }}
                    >
                        {isFixing ? <>⏳ Fixing...</> : <>⚡ Execute Fix</>}
                    </button>
                )}
            </div>
        </div>
    );
};

export default BattleCard;
